import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { recordPayment } from "../Services/AchatApi";
import { useUser } from "../Auth/useUser";
import moment from "moment";

const PaymentModal = ({ isOpen, onClose, achat, onSuccess }) => {
  const user = useUser();
  const infoUser = user.result;
  
  const [paymentData, setPaymentData] = useState({
    montant: "",
    mode_paiement: "espece",
    reference_paiement: "",
    date_paiement: moment().format("YYYY-MM-DD"),
    notes: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [remainingAmount, setRemainingAmount] = useState(0);

  useEffect(() => {
    if (achat) {
      const remaining = achat.total_ttc - (achat.montant_regle || 0);
      setRemainingAmount(remaining);
      setPaymentData(prev => ({
        ...prev,
        montant: remaining.toString() // Default to full remaining amount
      }));
    }
  }, [achat]);

  if (!isOpen || !achat) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const montant = parseFloat(paymentData.montant);
    if (isNaN(montant) || montant <= 0) {
      toast.error("Le montant doit être supérieur à 0");
      return;
    }
    
    if (montant > remainingAmount) {
      toast.error(`Le montant ne peut pas dépasser ${remainingAmount.toFixed(3)} TND`);
      return;
    }

    setLoading(true);
    
    try {
      const dataToSend = {
        ...paymentData,
        montant: montant,
        achat_id: achat.id,
        fournisseur_id: achat.fournisseur_details?.id,
        organisation_id: achat.organisation_id,
        created_by: infoUser.id,
      };
      
      const response = await recordPayment(achat.id, dataToSend);
      
      if (response.data.success) {
        toast.success("Paiement enregistré avec succès");
        onSuccess(response.data.data);
        onClose();
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.message || "Erreur lors de l'enregistrement du paiement");
    } finally {
      setLoading(false);
    }
  };

  const setFullAmount = () => {
    setPaymentData(prev => ({
      ...prev,
      montant: remainingAmount.toString()
    }));
  };

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Paiement - Facture {achat.numero_facture}
            </h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Invoice Summary */}
              <div className="card bg-light mb-3">
                <div className="card-body p-3">
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted">Fournisseur</small>
                      <div><strong>{achat.fournisseur_details?.nom}</strong></div>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Date facture</small>
                      <div>{new Date(achat.date_achat).toLocaleDateString('fr-FR')}</div>
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-6">
                      <small className="text-muted">Total TTC</small>
                      <div><strong>{parseFloat(achat.total_ttc).toFixed(3)} TND</strong></div>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Déjà réglé</small>
                      <div className="text-success">
                        <strong>{parseFloat(achat.montant_regle || 0).toFixed(3)} TND</strong>
                      </div>
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-12">
                      <small className="text-muted">Reste à payer</small>
                      <div className="text-danger h5 mb-0">
                        <strong>{remainingAmount.toFixed(3)} TND</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Form */}
              <div className="form-group">
                <label>Montant du paiement *</label>
                <div className="input-group">
                  <input
                    type="number"
                    step="0.001"
                    className="form-control"
                    name="montant"
                    value={paymentData.montant}
                    onChange={handleChange}
                    required
                  />
                  <div className="input-group-append">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={setFullAmount}
                      title="Régler le montant total"
                    >
                      Total
                    </button>
                  </div>
                </div>
                <small className="text-muted">
                  Maximum: {remainingAmount.toFixed(3)} TND
                </small>
              </div>

              <div className="form-group">
                <label>Mode de paiement *</label>
                <select
                  className="form-control"
                  name="mode_paiement"
                  value={paymentData.mode_paiement}
                  onChange={handleChange}
                  required
                >
                  <option value="espece">Espèces</option>
                  <option value="cheque">Chèque</option>
                  <option value="virement">Virement bancaire</option>
                  <option value="carte">Carte bancaire</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              <div className="form-group">
                <label>Date de paiement *</label>
                <input
                  type="date"
                  className="form-control"
                  name="date_paiement"
                  value={paymentData.date_paiement}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Référence (N° chèque, transaction, etc.)</label>
                <input
                  type="text"
                  className="form-control"
                  name="reference_paiement"
                  value={paymentData.reference_paiement}
                  onChange={handleChange}
                  placeholder="Optionnel"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  className="form-control"
                  rows="2"
                  name="notes"
                  value={paymentData.notes}
                  onChange={handleChange}
                  placeholder="Notes supplémentaires..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" />
                    Enregistrement...
                  </>
                ) : (
                  "Enregistrer le paiement"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;